import React, { useState } from 'react'

import DropdownIcon from '~/shared/icons/Logo'

import styles from '../styles.module.scss'
import { type BlogFiltersState, type SortOrder } from './BlogFilters'

const DATE_OPTIONS = [
  { value: 'month', label: 'This month' },
  { value: 'quarter', label: 'Last 3 months' },
]

const FilterDropdown: React.FC<{
  label: string
  options: string[]
  value: string | null
  onChange: (value: string | null) => void
}> = ({ label, options, value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false)
  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    const wrapper = e.currentTarget
    setTimeout(() => {
      if (!wrapper.contains(document.activeElement)) {
        setIsOpen(false)
      }
    }, 150)
  }

  return (
    <div className={styles.filterDropdown} onBlur={handleBlur} tabIndex={0}>
      <button
        type="button"
        className={`${styles.filterDropdownTrigger} ${value ? styles.filterDropdownTriggerActive : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={styles.filterLabel}>{label}</span>
        <span className={`${styles.filterArrow} ${isOpen ? styles.filterArrowOpen : ''}`}>
          <DropdownIcon color="currentColor" />
        </span>
      </button>
      {isOpen && (
        <ul className={styles.filterDropdownMenu}>
          <li
            onClick={() => {
              onChange(null)
              setIsOpen(false)
            }}
            className={!value ? styles.filterOptionActive : ''}
          >
            All
          </li>
          {options.map((opt) => (
            <li
              key={opt}
              onClick={() => {
                onChange(opt)
                setIsOpen(false)
              }}
              className={value === opt ? styles.filterOptionActive : ''}
            >
              {opt}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

interface BlogFilterBarProps {
  filters: BlogFiltersState
  onFiltersChange: (filters: BlogFiltersState) => void
  sortOrder: SortOrder
  onSortOrderChange: (order: SortOrder) => void
  regions: string[]
  topics: string[]
  categories: string[]
}

const BlogFilterBar: React.FC<BlogFilterBarProps> = ({
  filters,
  onFiltersChange,
  sortOrder,
  onSortOrderChange,
  regions,
  topics,
  categories,
}) => {
  return (
    <div className={styles.filtersWrapper}>
      <div className={styles.filtersRow}>
        <FilterDropdown
          label="Category"
          options={categories}
          value={filters.category}
          onChange={(v) => onFiltersChange({ ...filters, category: v })}
        />
        <span className={styles.filterDivider} />
        <FilterDropdown
          label="Date"
          options={DATE_OPTIONS.map((o) => o.label)}
          value={
            filters.date
              ? (DATE_OPTIONS.find((o) => o.value === filters.date)?.label ?? null)
              : null
          }
          onChange={(v) => {
            const opt = DATE_OPTIONS.find((o) => o.label === v)
            onFiltersChange({ ...filters, date: opt?.value ?? null })
          }}
        />
        <span className={styles.filterDivider} />
        <FilterDropdown
          label="Region"
          options={regions}
          value={filters.region}
          onChange={(v) => onFiltersChange({ ...filters, region: v })}
        />
        <span className={styles.filterDivider} />
        <FilterDropdown
          label="Topic"
          options={topics}
          value={filters.topic}
          onChange={(v) => onFiltersChange({ ...filters, topic: v })}
        />
        <div className={styles.sortDropdown}>
          <span className={styles.sortLabel}>Sort by:</span>
          <select
            value={sortOrder}
            onChange={(e) => onSortOrderChange(e.target.value as SortOrder)}
            className={styles.sortSelect}
          >
            <option value="newest">Date (newest to oldest)</option>
            <option value="oldest">Date (oldest to newest)</option>
          </select>
        </div>
      </div>
    </div>
  )
}

export default BlogFilterBar
